import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import api from "../../api";
import type { InvestmentSummaryItem, SolutionItem } from "../../api/types.ts";
import { generateDate, getWhat3WordsHref } from "./common.ts";
import styles from "./Ledger.module.css";

type SortColumn =
  | "purchaseDate"
  | "totalCredits"
  | "totalAreaSupportedM2"
  | "solutionName"
  | "clientName";

type SortDirection = "asc" | "desc";

type SoldUnit = {
  retirementCodeUnique: string;
  w3w: string;
};

type SoldUnitsResponse = {
  data: SoldUnit[];
  pagination?: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
  };
};

type ColumnDefinition = {
  key: SortColumn;
  label: string;
  className: string;
};

type PopupPosition = {
  top: number;
  left: number;
};

const PAGE_SIZE = 10;
const POPUP_PAGE_SIZE = 100;

const integerFormatter = new Intl.NumberFormat("en-GB");
const areaFormatter = new Intl.NumberFormat("en-GB", {
  maximumFractionDigits: 2,
});

const columns: ColumnDefinition[] = [
  {
    key: "purchaseDate",
    label: "Purchase Date",
    className: "colPurchaseDate",
  },
  { key: "totalCredits", label: "Credits", className: "colCredits" },
  {
    key: "totalAreaSupportedM2",
    label: "Total Area",
    className: "colTotalArea",
  },
  { key: "solutionName", label: "Project", className: "colProject" },
  { key: "clientName", label: "Buyer", className: "colBuyer" },
];

const getRowId = (row: InvestmentSummaryItem) =>
  `${row.clientId}-${row.solutionId}-${row.purchaseDate}`;

const compareRows = (
  a: InvestmentSummaryItem,
  b: InvestmentSummaryItem,
  sortBy: SortColumn,
) => {
  switch (sortBy) {
    case "purchaseDate":
      return a.purchaseDate - b.purchaseDate;
    case "totalCredits":
      return a.totalCredits - b.totalCredits;
    case "totalAreaSupportedM2":
      return a.totalAreaSupportedM2 - b.totalAreaSupportedM2;
    case "solutionName":
      return a.solutionName.localeCompare(b.solutionName);
    case "clientName":
      return a.clientName.localeCompare(b.clientName);
    default:
      return 0;
  }
};

const formatArea = (areaInM2: number) => `${areaFormatter.format(areaInM2)} m²`;

type LedgerProps = {
  tableLabelledById?: string;
};

function Ledger({ tableLabelledById }: LedgerProps) {
  const [sortBy, setSortBy] = useState<SortColumn>("purchaseDate");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [page, setPage] = useState(1);
  const [popupRow, setPopupRow] = useState<InvestmentSummaryItem | null>(null);
  const [popupAnchor, setPopupAnchor] = useState<HTMLButtonElement | null>(
    null,
  );
  const [popupPosition, setPopupPosition] = useState<PopupPosition>({
    top: 0,
    left: 0,
  });
  const popupRef = useRef<HTMLDivElement | null>(null);
  const tableCaptionId = useId();
  const popupId = useId();
  const popupTitleId = useId();
  const popupSummaryId = useId();

  const { data: summary = [], isPending } = useQuery<InvestmentSummaryItem[]>({
    queryKey: ["investmentSummary"],
    queryFn: api.get.investmentSummary,
    throwOnError: true,
  });

  const { data: solutions = [] } = useQuery<SolutionItem[]>({
    queryKey: ["solutions"],
    queryFn: api.get.solutions,
    staleTime: 300_000,
  });

  const solutionDisplayNames = new Map(
    solutions
      .filter((s) => s.id && s.vbcWebsiteDisplayName)
      .map((s) => [s.id!, s.vbcWebsiteDisplayName!]),
  );

  const sortedSummary = [...summary].sort((a, b) => {
    const baseCompare = compareRows(a, b, sortBy);
    const directed = sortDirection === "asc" ? baseCompare : -baseCompare;
    if (directed !== 0) return directed;
    // Stable tiebreaker: fall back to purchaseDate desc, then clientId
    return a.purchaseDate !== b.purchaseDate
      ? b.purchaseDate - a.purchaseDate
      : a.clientId.localeCompare(b.clientId);
  });

  const totalPages = Math.max(1, Math.ceil(sortedSummary.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const start = (page - 1) * PAGE_SIZE;
  const visibleRows = sortedSummary.slice(start, start + PAGE_SIZE);
  const popupRowId = popupRow ? getRowId(popupRow) : null;

  const closePopup = useCallback(
    (restoreFocus = false) => {
      const anchor = popupAnchor;
      setPopupRow(null);
      setPopupAnchor(null);

      if (restoreFocus) {
        anchor?.focus();
      }
    },
    [popupAnchor],
  );

  const handlePopupToggle = (
    row: InvestmentSummaryItem,
    anchor: HTMLButtonElement,
  ) => {
    const rowId = getRowId(row);

    if (rowId === popupRowId) {
      closePopup();
      return;
    }

    setPopupRow(row);
    setPopupAnchor(anchor);
  };

  useEffect(() => {
    if (!popupRow || !popupAnchor) {
      return;
    }

    const positionPopup = () => {
      const anchorRect = popupAnchor.getBoundingClientRect();
      const popupWidth = popupRef.current?.offsetWidth ?? 416;
      const popupHeight = popupRef.current?.offsetHeight ?? 440;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const safeLeft = Math.max(
        12,
        Math.min(
          anchorRect.right - popupWidth,
          viewportWidth - popupWidth - 12,
        ),
      );
      const spaceBelow = viewportHeight - anchorRect.bottom - 12;
      const spaceAbove = anchorRect.top - 12;
      const shouldPlaceAbove =
        popupHeight > spaceBelow && spaceAbove > spaceBelow;
      const safeTop = shouldPlaceAbove
        ? Math.max(12, anchorRect.top - popupHeight - 8)
        : Math.min(anchorRect.bottom + 8, viewportHeight - popupHeight - 12);

      setPopupPosition({
        top: safeTop,
        left: safeLeft,
      });
    };

    positionPopup();
    window.addEventListener("scroll", positionPopup, true);
    window.addEventListener("resize", positionPopup);

    return () => {
      window.removeEventListener("scroll", positionPopup, true);
      window.removeEventListener("resize", positionPopup);
    };
  }, [popupRow, popupAnchor]);

  useEffect(() => {
    if (!popupRow || !popupAnchor || typeof document === "undefined") {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;

      if (!(target instanceof Node)) {
        return;
      }

      if (popupRef.current?.contains(target) || popupAnchor.contains(target)) {
        return;
      }

      closePopup();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      closePopup(true);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closePopup, popupAnchor, popupRow]);

  useEffect(() => {
    if (!popupRow) {
      return;
    }

    popupRef.current?.focus();
  }, [popupRow]);

  const soldUnitsQuery = useQuery<SoldUnitsResponse>({
    queryKey: ["soldVBCUnits", popupRow?.clientId, popupRow?.solutionId],
    queryFn: async () => {
      if (!popupRow) {
        return { data: [] };
      }

      const response = await api.get.soldVBCUnits({
        clientId: popupRow.clientId,
        solutionId: popupRow.solutionId,
        page: 1,
        limit: POPUP_PAGE_SIZE,
      });
      return response as SoldUnitsResponse;
    },
    enabled: Boolean(popupRow),
    staleTime: 60_000,
  });

  const handleSort = (column: SortColumn) => {
    closePopup();
    setPage(1);
    if (column === sortBy) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setSortBy(column);
    setSortDirection("asc");
  };

  const getSortDirectionLabel = (column: SortColumn) => {
    if (sortBy !== column) {
      return "none";
    }

    return sortDirection === "asc" ? "ascending" : "descending";
  };

  const getSortButtonLabel = (column: ColumnDefinition) => {
    const currentDirection = getSortDirectionLabel(column.key);

    if (currentDirection === "none") {
      return `Sort by ${column.label}. Currently unsorted.`;
    }

    const nextDirection =
      currentDirection === "ascending" ? "descending" : "ascending";

    return `${column.label}, sorted ${currentDirection}. Activate to sort ${nextDirection}.`;
  };

  if (isPending) {
    return <p className={styles.loadingState}>Loading ledger...</p>;
  }

  const soldUnits = soldUnitsQuery.data?.data ?? [];
  const soldUnitsTotal =
    soldUnitsQuery.data?.pagination?.totalItems ?? soldUnits.length;
  const isSoldUnitsTruncated = soldUnitsTotal > soldUnits.length;
  const popupElement =
    popupRow && popupAnchor ? (
      <div
        ref={popupRef}
        className={styles.popup}
        style={{
          top: `${popupPosition.top}px`,
          left: `${popupPosition.left}px`,
        }}
        id={popupId}
        role="dialog"
        aria-modal="false"
        aria-labelledby={popupTitleId}
        aria-describedby={popupSummaryId}
        tabIndex={-1}
      >
        <div className={styles.popupHeader}>
          <div className={styles.popupTitleBlock}>
            <p id={popupTitleId} className={styles.popupTitle}>
              Sold units
            </p>
            <p id={popupSummaryId} className={styles.popupSummary}>
              {soldUnitsQuery.isFetching
                ? "Loading unit records..."
                : isSoldUnitsTruncated
                  ? `Showing first ${soldUnits.length} of ${soldUnitsTotal} unit records.`
                  : `${soldUnitsTotal} unit records.`}
            </p>
          </div>
          <button
            type="button"
            className={styles.popupCloseButton}
            onClick={() => closePopup(true)}
          >
            Close
          </button>
        </div>
        <div className={styles.popupColumns} aria-hidden="true">
          <span>Retirement code</span>
          <span>W3W location</span>
        </div>
        <div className={styles.popupBody}>
          {soldUnitsQuery.isFetching ? (
            <p className={styles.popupMessage}>Loading units...</p>
          ) : soldUnits.length === 0 ? (
            <p className={styles.popupMessage}>No unit records found.</p>
          ) : (
            soldUnits.map((unit) => (
              <div
                className={styles.popupRow}
                key={`${popupRowId}-${unit.retirementCodeUnique}-${unit.w3w}`}
              >
                <span>{unit.retirementCodeUnique}</span>
                <a
                  href={getWhat3WordsHref(unit.w3w)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {unit.w3w}
                </a>
              </div>
            ))
          )}
        </div>
      </div>
    ) : null;

  return (
    <section className={styles.surface}>
      <div className={styles.scrollFrame}>
        <div className={styles.tableLayout}>
          <div
            className={styles.floatingHeader}
            role="group"
            aria-label="Ledger sort controls"
          >
            {columns.map((column) => {
              const isActive = sortBy === column.key;
              const indicator = isActive
                ? sortDirection === "asc"
                  ? "↑"
                  : "↓"
                : "↕";
              return (
                <button
                  key={column.key}
                  type="button"
                  className={`${styles.headerButton} ${styles[column.className]} ${
                    isActive ? styles.headerButtonActive : ""
                  }`}
                  onClick={() => handleSort(column.key)}
                  aria-controls="ledger-table"
                  aria-label={getSortButtonLabel(column)}
                >
                  <span>{column.label}</span>
                  <span className={styles.sortIndicator} aria-hidden="true">
                    {indicator}
                  </span>
                </button>
              );
            })}
          </div>

          <div className={styles.tableShell}>
            <table
              id="ledger-table"
              className={styles.table}
              aria-labelledby={tableLabelledById ?? tableCaptionId}
            >
              <caption id={tableCaptionId} className={styles.visuallyHidden}>
                Retired voluntary biodiversity credits
              </caption>
              <colgroup>
                <col className={styles.colPurchaseDate} />
                <col className={styles.colCredits} />
                <col className={styles.colTotalArea} />
                <col className={styles.colProject} />
                <col className={styles.colBuyer} />
              </colgroup>
              <thead className={styles.visuallyHidden}>
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      scope="col"
                      aria-sort={getSortDirectionLabel(column.key)}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="ledger-table-body">
                {visibleRows.map((row) => {
                  const rowId = getRowId(row);
                  const rowWithPopup = rowId === popupRowId;

                  return (
                    <tr
                      key={rowId}
                      className={`${styles.dataRow} ${
                        rowWithPopup ? styles.dataRowActive : ""
                      }`}
                    >
                      <td className={styles.cell}>
                        {generateDate(row.purchaseDate)}
                      </td>
                      <td className={styles.cell}>
                        {integerFormatter.format(row.totalCredits)}
                      </td>
                      <td className={styles.cell}>
                        {formatArea(row.totalAreaSupportedM2)}
                      </td>
                      <td className={styles.cell}>
                        {solutionDisplayNames.get(row.solutionId) ??
                          row.solutionName}
                      </td>
                      <td className={styles.cell}>
                        <div className={styles.buyerCell}>
                          <span>{row.clientName}</span>
                          <button
                            type="button"
                            className={styles.rowActionButton}
                            aria-expanded={rowWithPopup}
                            aria-controls={rowWithPopup ? popupId : undefined}
                            aria-haspopup="dialog"
                            onClick={(event) =>
                              handlePopupToggle(row, event.currentTarget)
                            }
                          >
                            {rowWithPopup ? "Hide units" : "View units"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {visibleRows.length === 0 ? (
              <p className={styles.emptyState}>No summary records available.</p>
            ) : null}
          </div>
        </div>
      </div>

      {totalPages > 1 ? (
        <nav className={styles.pagination} aria-label="Ledger pagination">
          <button
            type="button"
            className={styles.paginationButton}
            onClick={() => {
              closePopup();
              setPage((current) => Math.max(1, current - 1));
            }}
            disabled={page === 1}
          >
            Previous
          </button>
          <div className={styles.pageNumbers}>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map(
              (pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  className={`${styles.paginationButton} ${
                    page === pageNumber ? styles.paginationButtonActive : ""
                  }`}
                  onClick={() => {
                    closePopup();
                    setPage(pageNumber);
                  }}
                  aria-current={page === pageNumber ? "page" : undefined}
                >
                  {pageNumber}
                </button>
              ),
            )}
          </div>
          <button
            type="button"
            className={styles.paginationButton}
            onClick={() => {
              closePopup();
              setPage((current) => Math.min(totalPages, current + 1));
            }}
            disabled={page === totalPages}
          >
            Next
          </button>
        </nav>
      ) : null}

      {popupElement && typeof document !== "undefined"
        ? createPortal(popupElement, document.body)
        : null}
    </section>
  );
}

export default Ledger;
